'use client';

import React, { useMemo, useState } from 'react';
import type { Reminder } from '@/core/domain';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Button } from '@/core/presentation/components/ui/Button';
import {
  MANUTENCOES_AGENDADAS_CAL_CLEAR_DAY,
  MANUTENCOES_AGENDADAS_CAL_LEGEND_COUNT,
  MANUTENCOES_AGENDADAS_CAL_LEGEND_TITLE,
  MANUTENCOES_AGENDADAS_CAL_NEXT_MONTH,
  MANUTENCOES_AGENDADAS_CAL_PREV_MONTH,
  MANUTENCOES_AGENDADAS_CAL_TITLE,
} from '@/features/app/presentation/constants/manutencoes-agendadas-copy';
import { countScheduledMaintenanceOnDay } from '@/features/app/presentation/utils/reminder-day-metrics';
import {
  calendarDayButtonClassName,
  calendarDayNumberClassName,
  isCalendarToday,
} from '@/features/app/presentation/utils/calendar-day-cell';

const SCHED_CAL_CELL_H = 'min-h-[4rem] sm:min-h-[5rem]';

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function dateKeyFromParts(year: number, monthIndex: number, day: number): string {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

function buildMonthCells(year: number, monthIndex: number): (number | null)[] {
  const first = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const startOffset = first.getDay();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i += 1) {
    cells.push(null);
  }
  for (let d = 1; d <= lastDay; d += 1) {
    cells.push(d);
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

export interface ScheduledMaintenanceCalendarProps {
  /** Apenas lembretes com `scheduled_date` (já filtrados). */
  scheduledReminders: Reminder[];
  selectedDayKey: string | null;
  onSelectDay: (dayKey: string | null) => void;
  belowLegend?: React.ReactNode;
}

export const ScheduledMaintenanceCalendar: React.FC<ScheduledMaintenanceCalendarProps> = ({
  scheduledReminders,
  selectedDayKey,
  onSelectDay,
  belowLegend,
}) => {
  const [view, setView] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const year = view.getFullYear();
  const monthIndex = view.getMonth();

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
        new Date(year, monthIndex, 1)
      ),
    [year, monthIndex]
  );

  const cells = useMemo(() => buildMonthCells(year, monthIndex), [year, monthIndex]);

  const goPrev = () => {
    setView(new Date(year, monthIndex - 1, 1));
  };

  const goNext = () => {
    setView(new Date(year, monthIndex + 1, 1));
  };

  return (
    <div className="rounded-xl border border-border-light bg-white p-3 sm:p-4 space-y-3 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-navy-dark flex items-center gap-2">
          <AppIcon name="CalendarDaysIcon" size={18} className="text-cyan shrink-0" />
          {MANUTENCOES_AGENDADAS_CAL_TITLE}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="!px-2"
            aria-label={MANUTENCOES_AGENDADAS_CAL_PREV_MONTH}
            onClick={goPrev}
          >
            <AppIcon name="ChevronLeftIcon" size={18} />
          </Button>
          <span className="text-sm font-medium text-navy-dark capitalize min-w-[10rem] text-center px-1">
            {monthLabel}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="!px-2"
            aria-label={MANUTENCOES_AGENDADAS_CAL_NEXT_MONTH}
            onClick={goNext}
          >
            <AppIcon name="ChevronRightIcon" size={18} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] sm:text-xs font-medium text-text-muted uppercase tracking-wide">
        {WEEK_DAYS.map((d) => (
          <div key={d} className="py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day == null) {
            return <div key={`empty-${idx}`} className={SCHED_CAL_CELL_H} />;
          }
          const dayKey = dateKeyFromParts(year, monthIndex, day);
          const count = countScheduledMaintenanceOnDay(scheduledReminders, dayKey);
          const selected = selectedDayKey === dayKey;
          const hasData = count > 0;
          const todayCell = isCalendarToday(dayKey);
          const metricStrong = todayCell ? 'text-white' : 'text-navy-dark';

          return (
            <button
              key={dayKey}
              type="button"
              onClick={() => onSelectDay(selected ? null : dayKey)}
              className={calendarDayButtonClassName({
                dayKey,
                selected,
                hasData,
                cellMinHeightClass: SCHED_CAL_CELL_H,
              })}
              aria-pressed={selected}
              aria-label={`Dia ${pad2(day)}: ${count} manutenção(ões) agendada(s)`}
            >
              <span className={`block text-sm font-semibold ${calendarDayNumberClassName(dayKey, selected)}`}>
                {day}
              </span>
              {hasData ? (
                <div className="mt-1.5 flex items-center gap-1.5 min-w-0" aria-hidden="true">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" title="Agendadas" />
                  <span className={`text-[10px] font-semibold tabular-nums ${metricStrong} leading-none`}>
                    {count}
                  </span>
                </div>
              ) : (
                <span
                  className={`mt-1.5 block text-[10px] leading-none ${
                    todayCell ? 'text-white/55' : 'text-text-muted/70'
                  }`}
                >
                  —
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selectedDayKey ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-cyan"
            onClick={() => onSelectDay(null)}
          >
            {MANUTENCOES_AGENDADAS_CAL_CLEAR_DAY}
          </Button>
        </div>
      ) : null}

      <div
        className="rounded-lg border border-border-light bg-[#fafbfc] px-3 py-2.5 space-y-2 text-[11px] sm:text-xs text-text-muted"
        role="region"
        aria-label={MANUTENCOES_AGENDADAS_CAL_LEGEND_TITLE}
      >
        <p className="font-semibold text-navy-dark text-xs">{MANUTENCOES_AGENDADAS_CAL_LEGEND_TITLE}</p>
        <ul className="space-y-1.5 list-none pl-0 m-0">
          <li className="flex gap-2">
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
            <span>
              <strong className="text-navy-dark">Total</strong> — {MANUTENCOES_AGENDADAS_CAL_LEGEND_COUNT}
            </span>
          </li>
        </ul>
      </div>

      {belowLegend != null ? (
        <div className="pt-1 border-t border-border-light min-w-0">{belowLegend}</div>
      ) : null}
    </div>
  );
};
